/**
 * Web push for the console installed as an app (design 12.5). The service worker is public/sw.js.
 *
 * Turning push on: check support, ask the server for its public (VAPID) key, ask the browser for permission,
 * subscribe through the service worker, then register the subscription with the server. The server sends
 * pushes to every registered device of the person, and drops a device when its push service says it is gone.
 *
 * iOS and iPadOS: push works only once the console is added to the home screen (Share, Add to Home Screen)
 * and opened from there, on iOS 16.4 or later. In a Safari tab there is no PushManager at all.
 */
import { api } from '../api'

export const IOS_NOTE = 'On iPhone and iPad, notifications work only when the console is added to the home screen and opened from there (iOS 16.4 or later). In Safari, tap Share, then Add to Home Screen.'

const ON_KEY = 't4.push.on'
const SYNC_KEY = 't4.push.synced'
const RESYNC_MS = 24 * 3600_000

// ---------------------------------------------------------------- Environment
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

export type PushSupport =
  | { state: 'ok' }
  | { state: 'ios-install' }
  | { state: 'insecure' }
  | { state: 'unsupported'; missing: string }

/** Whether this browser can receive push here and now. */
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return { state: 'unsupported', missing: 'a browser' }
  if (!window.isSecureContext) return { state: 'insecure' }
  const hasSw = 'serviceWorker' in navigator
  const hasPush = 'PushManager' in window
  const hasNotif = 'Notification' in window
  if (isIos() && !isStandalone() && !(hasPush && hasNotif)) return { state: 'ios-install' }
  if (!hasSw) return { state: 'unsupported', missing: 'service workers' }
  if (!hasPush) return { state: 'unsupported', missing: 'push messages' }
  if (!hasNotif) return { state: 'unsupported', missing: 'notifications' }
  return { state: 'ok' }
}

/** The browser's notification permission, or 'unsupported'. */
export function permission(): NotificationPermission | 'unsupported' {
  return typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
}

/** How to undo a "Block" in the common browsers. */
export function deniedHelp(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  if (isIos()) return 'Open the Settings app, then Notifications, find takeit4me and allow notifications.'
  if (/Firefox\//.test(ua)) return 'Click the icon to the left of the address bar, remove the block on notifications, then reload this page.'
  if (/Safari\//.test(ua) && !/Chrome|Chromium|Edg\//.test(ua)) return 'Open Safari, Settings, Websites, Notifications, and set this site to Allow.'
  return 'Click the icon to the left of the address bar, open Site settings and set Notifications to Allow. Then reload this page.'
}

// ---------------------------------------------------------------- Service worker
/** Registers /sw.js (scope "/"). Called once from main.tsx; safe to call again. */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !window.isSecureContext) return Promise.resolve(null)
  return navigator.serviceWorker.register('/sw.js', { scope: '/', updateViaCache: 'none' }).catch((e: unknown) => {
    console.warn('Service worker registration failed', e)
    return null
  })
}

/** The active registration, registering it if needed. Push needs an active worker, so this waits for it. */
async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/')
  if (!existing) {
    const r = await registerServiceWorker()
    if (!r) throw new Error('This browser would not start the background service the console needs for notifications.')
  }
  const ready = navigator.serviceWorker.ready
  const timeout = new Promise<never>((_, rej) => setTimeout(() => rej(new Error('The background service took too long to start. Reload the page and try again.')), 15_000))
  return Promise.race([ready, timeout])
}

/** This device's push subscription, if any. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport().state !== 'ok') return null
  const reg = await navigator.serviceWorker.getRegistration('/')
  return (await reg?.pushManager.getSubscription()) ?? null
}

// ---------------------------------------------------------------- Subscribe and unsubscribe
export function urlBase64ToUint8Array(b64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4)
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

function sameKey(sub: PushSubscription, key: Uint8Array): boolean {
  const k = sub.options?.applicationServerKey
  if (!k) return true // the browser does not say; assume it matches
  const a = new Uint8Array(k)
  return a.length === key.length && a.every((x, i) => x === key[i])
}

function toBody(sub: PushSubscription) {
  const j = sub.toJSON()
  const keys = j.keys ?? {}
  if (!j.endpoint || !keys.p256dh || !keys.auth) throw new Error('The browser returned an incomplete push subscription. Try again.')
  return { endpoint: j.endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth }, user_agent: navigator.userAgent.slice(0, 300) }
}

async function register(sub: PushSubscription) {
  await api.subscribePush(toBody(sub))
  try { localStorage.setItem(SYNC_KEY, JSON.stringify({ endpoint: sub.endpoint, at: Date.now() })) } catch { /* private mode */ }
}

export type EnableResult =
  | { ok: true }
  | { ok: false; reason: 'unsupported' | 'ios-install' | 'not-configured' | 'denied' | 'dismissed' | 'error'; message: string }

/** Turns push on for this device. Must be called from a click (browsers only ask for permission then). */
export async function enablePush(): Promise<EnableResult> {
  const sup = pushSupport()
  if (sup.state === 'ios-install') return { ok: false, reason: 'ios-install', message: IOS_NOTE }
  if (sup.state === 'insecure') return { ok: false, reason: 'unsupported', message: 'Notifications need a secure (https) connection.' }
  if (sup.state === 'unsupported') return { ok: false, reason: 'unsupported', message: `This browser does not support ${sup.missing}. Try Chrome, Edge, Firefox or Safari.` }
  try {
    // Ask for permission first, straight from the click, before any network wait.
    const perm = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission
    if (perm === 'denied') return { ok: false, reason: 'denied', message: `Notifications are blocked for this site. ${deniedHelp()}` }
    if (perm !== 'granted') return { ok: false, reason: 'dismissed', message: 'You closed the browser\'s question without allowing notifications. Try again and choose Allow.' }
    const pk = await api.pushKey()
    if (!pk.available || !pk.public_key) return { ok: false, reason: 'not-configured', message: 'Push notifications are not set up on this service yet. Email and text still work.' }
    const key = urlBase64ToUint8Array(pk.public_key)
    const reg = await activeRegistration()
    let sub = await reg.pushManager.getSubscription()
    if (sub && !sameKey(sub, key)) { await sub.unsubscribe().catch(() => false); sub = null }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key })
    await register(sub)
    try { localStorage.setItem(ON_KEY, '1') } catch { /* private mode */ }
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: 'error', message: e instanceof Error ? e.message : String(e) }
  }
}

/** Turns push off for this device: the server forgets it, then the browser drops the subscription. */
export async function disablePush(): Promise<void> {
  try { localStorage.removeItem(ON_KEY); localStorage.removeItem(SYNC_KEY) } catch { /* private mode */ }
  const sub = await currentSubscription()
  if (!sub) return
  try { await api.unsubscribePush(sub.endpoint) } finally { await sub.unsubscribe().catch(() => false) }
}

/**
 * Keeps the server's record of this device current. Runs after sign-in and when the service worker says
 * the subscription changed (it cannot tell the server itself: it has no token). Re-registers the current
 * subscription when it is new or not confirmed for a day; re-subscribes when the person turned push on here
 * but the browser dropped the subscription.
 */
export async function syncPush(opts: { force?: boolean; oldEndpoint?: string | null } = {}): Promise<void> {
  if (pushSupport().state !== 'ok' || Notification.permission !== 'granted') return
  let wanted = false
  let last: { endpoint?: string; at?: number } = {}
  try { wanted = localStorage.getItem(ON_KEY) === '1'; last = JSON.parse(localStorage.getItem(SYNC_KEY) ?? '{}') } catch { /* private mode */ }
  const reg = await navigator.serviceWorker.getRegistration('/')
  if (!reg) return
  let sub = await reg.pushManager.getSubscription()
  if (opts.oldEndpoint && opts.oldEndpoint !== sub?.endpoint) await api.unsubscribePush(opts.oldEndpoint).catch(() => undefined)
  if (!sub) {
    if (!wanted) return
    const pk = await api.pushKey()
    if (!pk.available || !pk.public_key) return
    await activeRegistration()
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(pk.public_key) })
  }
  if (!opts.force && last.endpoint === sub.endpoint && Date.now() - (last.at ?? 0) < RESYNC_MS) return
  await register(sub)
}

// ---------------------------------------------------------------- Messages from the service worker
export type SwMessage =
  | { type: 't4:push'; id: string | null }
  | { type: 't4:navigate'; url: string; id: string | null }
  | { type: 't4:resubscribe'; endpoint: string | null; old_endpoint: string | null }

type SwListener = (m: SwMessage) => void
const swListeners = new Set<SwListener>()
const pending: SwMessage[] = []
let listening = false

/**
 * Starts listening to the service worker. Called at module load from main.tsx, before React mounts, so a
 * message sent to a window that is just opening is buffered rather than lost.
 */
export function listenToServiceWorker() {
  if (listening || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  listening = true
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent) => {
    const m = e.data as SwMessage | undefined
    if (!m || typeof m !== 'object' || typeof m.type !== 'string' || !m.type.startsWith('t4:')) return
    if (swListeners.size) for (const l of swListeners) l(m)
    else pending.push(m)
  })
  navigator.serviceWorker.startMessages?.()
}

/** Subscribe to service worker messages; buffered ones are delivered straight away. */
export function onServiceWorkerMessage(fn: SwListener): () => void {
  swListeners.add(fn)
  for (const m of pending.splice(0)) fn(m)
  return () => { swListeners.delete(fn) }
}

/** Sets the app icon badge where the platform supports it (installed apps). */
export function setAppBadge(n: number) {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> }
  try {
    if (n > 0) void nav.setAppBadge?.(n)?.catch(() => undefined)
    else void nav.clearAppBadge?.()?.catch(() => undefined)
  } catch { /* not supported */ }
}
